import { authenticatedFetch } from '@bettercampus/global/runtime';

const SW_READY_TIMEOUT_MS = 10_000;

function getOsFromUserAgent(ua: string): string {
  if (/iPad/i.test(ua)) return 'iPad';
  if (/iPhone|iPod/i.test(ua)) return 'iPhone';
  if (/Android/i.test(ua)) return 'Android';
  if (/Mac OS X|Macintosh/i.test(ua)) return 'macOS';
  if (/Win(dows|10|11)/i.test(ua) || /Windows NT/i.test(ua)) return 'Windows';
  if (/CrOS/i.test(ua)) return 'Chrome OS';
  if (/Linux/i.test(ua)) return 'Linux';
  return 'unknown';
}

/** Client Hints (Chromium only) can provide device model e.g. "Pixel 7", "SM-G960". Not available in Safari/iOS. */
async function getClientHints(): Promise<{ model?: string; platformVersion?: string }> {
  const nav = navigator as Navigator & {
    userAgentData?: { getHighEntropyValues(values: string[]): Promise<Record<string, string>> };
  };
  if (!nav.userAgentData?.getHighEntropyValues) return {};
  try {
    const hints = await nav.userAgentData.getHighEntropyValues(['model', 'platformVersion']);
    return {
      ...(hints.model && { model: hints.model }),
      ...(hints.platformVersion && { platformVersion: hints.platformVersion }),
    };
  } catch {
    return {};
  }
}

function getDeviceInfo(): {
  platform: string;
  userAgent: string;
  deviceType: string;
  os: string;
  model?: string;
  platformVersion?: string;
} | null {
  if (typeof navigator === 'undefined') return null;
  const ua = navigator.userAgent;
  const deviceType = /iPhone|iPad|iPod|Android/i.test(ua)
    ? 'mobile'
    : /Mobile/i.test(ua)
      ? 'mobile'
      : 'desktop';
  return {
    platform: navigator.platform ?? '',
    userAgent: ua,
    deviceType,
    os: getOsFromUserAgent(ua),
  };
}

export function getPwaPushApiBase(): string {
  return (import.meta.env.VITE_PWA_PUSH_API_URL || '').replace(/\/$/, '');
}

export type NotificationPermissionState = 'granted' | 'denied' | 'default' | 'unsupported';

/**
 * Current browser/OS-level notification permission. Use this to detect when the user
 * has blocked notifications for the site (e.g. Chrome → Site settings → Block).
 * No API exists to detect "silenced" / Do Not Disturb / Focus mode.
 */
export function getNotificationPermission(): NotificationPermissionState {
  if (typeof window === 'undefined' || !('Notification' in window)) return 'unsupported';
  const p = Notification.permission;
  if (p === 'granted' || p === 'denied' || p === 'default') return p;
  return 'unsupported';
}

export type OsCategory = 'ios' | 'android' | 'desktop';

/**
 * Rough OS category for UI hints (e.g. where to re-enable notifications).
 * Uses userAgent; not exported from getDeviceInfo to avoid pulling that into the page.
 */
export function getOsCategory(): OsCategory {
  if (typeof navigator === 'undefined') return 'desktop';
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'desktop';
}

/** OS-specific copy for when the user has blocked notifications at the browser level. */
export function getBlockedHintCopy(os: OsCategory): string {
  switch (os) {
    case 'ios':
      return 'Notifications are blocked for this site. To receive alerts, go to Settings → Safari (or this app) → Notifications and allow them.';
    case 'android':
      return 'Notifications are blocked for this site. To receive alerts, open Chrome → Settings → Site settings → Notifications and allow this site.';
    default:
      return 'Notifications are blocked for this site in your browser. To receive alerts, allow notifications in your browser settings (e.g. Chrome → Site settings → Notifications).';
  }
}

function waitForServiceWorker(): Promise<ServiceWorkerRegistration> {
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('Service worker timed out')), SW_READY_TIMEOUT_MS)
    ),
  ]);
}

/**
 * Requests notification permission, waits for SW, subscribes to push, and registers with backend.
 * Returns null on success, or an error message string.
 */
export async function enablePushSubscription(apiBase: string): Promise<string | null> {
  if (!('Notification' in window) || !('PushManager' in window)) {
    return 'Push not supported in this browser';
  }

  const permission =
    Notification.permission === 'granted' ? 'granted' : await Notification.requestPermission();

  if (permission !== 'granted') return 'Notification permission denied';

  let reg: ServiceWorkerRegistration;
  try {
    reg = await waitForServiceWorker();
  } catch {
    return 'Service worker not ready — reload and try again';
  }

  if (reg.active?.state !== 'activated') {
    await new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(
        () => reject(new Error('SW activation timed out')),
        SW_READY_TIMEOUT_MS
      );
      const done = () => {
        clearTimeout(timeout);
        resolve();
      };
      const checkState = () => {
        if (reg.active?.state === 'activated') done();
      };
      (reg.installing ?? reg.waiting ?? reg.active)?.addEventListener('statechange', checkState);
      navigator.serviceWorker.addEventListener('controllerchange', done, { once: true });
    });
  }

  const keyRes = await fetch(`${apiBase}/pwa-push/vapid-public-key`);
  if (!keyRes.ok) return `Failed to fetch VAPID key: ${keyRes.status}`;

  const data = (await keyRes.json()) as { publicKey?: string; message?: { publicKey?: string } };
  const publicKey =
    typeof data?.publicKey === 'string'
      ? data.publicKey
      : typeof data?.message?.publicKey === 'string'
        ? data.message.publicKey
        : null;

  if (!publicKey) return 'Invalid VAPID public key from backend';

  let b64 = publicKey.replace(/-/g, '+').replace(/_/g, '/');
  const pad = b64.length % 4;
  if (pad) b64 += '='.repeat(4 - pad);
  const keyBytes = Uint8Array.from(atob(b64), c => c.charCodeAt(0));

  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: keyBytes,
  });

  const device = getDeviceInfo();
  if (device) {
    const hints = await getClientHints();
    if (hints.model) device.model = hints.model;
    if (hints.platformVersion) device.platformVersion = hints.platformVersion;
  }
  const subscribeRes = await authenticatedFetch(`${apiBase}/pwa-push/subscribe`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ subscription: sub.toJSON(), ...(device && { device }) }),
  });

  if (!subscribeRes) return 'Backend subscribe failed';
  await subscribeRes.json().catch(() => ({}));
  return null;
}

/**
 * Clears the stored push subscription on the backend (profiles.pwa_subscriptions).
 * Returns null on success, or an error message string.
 */
export async function disablePushSubscription(apiBase: string): Promise<string | null> {
  const res = await authenticatedFetch(`${apiBase}/pwa-push/unsubscribe`, { method: 'POST' });
  if (!res) return 'Backend unsubscribe failed';
  if (!res.ok) return `Unsubscribe failed: ${res.status}`;
  return null;
}
